// Courses2.js
import React, { useState, useEffect } from "react";
import classNames from "classnames";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupText,
  Card,
  CardImg,
  CardBody,
  Button,
  Badge,
  Progress,
  Toast,
  ToastHeader,
  ToastBody,
} from "reactstrap";
import {
  FaBookOpen,
  FaUserGraduate,
  FaSearch,
  FaPlusCircle,
  FaCheckCircle,
  FaPlay,
  FaTimes,
} from "react-icons/fa";

/* ------------------------------------------------------------------ */
/* 1. Sample course data (replace with API or DB data in production)   */
/* ------------------------------------------------------------------ */
const COURSES = [
  {
    id: 1,
    title: "Web Development Fundamentals",
    instructor: "Sarah Johnson",
    duration: "6 weeks",
    level: "Beginner",
    description:
      "Learn the basics of HTML, CSS, and JavaScript to build your first website.",
    image:
      "https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 2,
    title: "Advanced JavaScript Patterns",
    instructor: "Michael Chen",
    duration: "8 weeks",
    level: "Advanced",
    description:
      "Master design patterns, closures, and advanced concepts in JavaScript.",
    image:
      "https://images.unsplash.com/photo-1579468118864-1b9ea3c0db4a?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 3,
    title: "UI/UX Design Principles",
    instructor: "Emma Rodriguez",
    duration: "5 weeks",
    level: "Intermediate",
    description:
      "Learn to create intuitive and beautiful user interfaces and experiences.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 4,
    title: "Python for Data Science",
    instructor: "David Wilson",
    duration: "10 weeks",
    level: "Intermediate",
    description:
      "Use Python to analyze and visualize data with pandas, NumPy, and Matplotlib.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 5,
    title: "Mobile App Development with Flutter",
    instructor: "Lisa Wong",
    duration: "7 weeks",
    level: "Intermediate",
    description:
      "Build cross-platform mobile apps using Google's Flutter framework.",
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 6,
    title: "DevOps Essentials",
    instructor: "Robert Taylor",
    duration: "6 weeks",
    level: "Advanced",
    description:
      "Learn CI/CD pipelines, containerization, and cloud deployment strategies.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
  },
];

/* ------------------------------------------------------------------ */
/* 2. Helper to get bootstrap badge color for each level              */
/* ------------------------------------------------------------------ */
const levelColor = (level) => {
  switch (level.toLowerCase()) {
    case "beginner":
      return "success";
    case "intermediate":
      return "warning";
    case "advanced":
      return "danger";
    default:
      return "primary";
  }
};

/* ------------------------------------------------------------------ */
/* 3. Course Card component                                            */
/* ------------------------------------------------------------------ */
const CourseCard = ({
  course,
  isSubscribed,
  onSubscribe,
  onContinue,
  onUnsubscribe,
  showProgress,
}) => (
  <Card className="shadow-sm border-0 h-100 course-card">
    <CardImg
      top
      src={course.image}
      alt={course.title}
      style={{ height: "12rem", objectFit: "cover" }}
    />
    <CardBody className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <h5 className="mb-0">{course.title}</h5>
        <Badge color={levelColor(course.level)} pill>
          {course.level}
        </Badge>
      </div>
      <p className="text-muted mb-2">
        By {course.instructor} • {course.duration}
      </p>
      <p className="flex-grow-1">{course.description}</p>

      {/* Progress bar (My Courses only) */}
      {showProgress && (
        <div className="mb-3">
          <div className="d-flex justify-content-between small text-muted">
            <span>Progress</span>
            <span>{course.progress}%</span>
          </div>
          <Progress value={course.progress} />
        </div>
      )}

      {/* Action buttons */}
      {isSubscribed ? (
        <div className={showProgress ? "d-flex" : ""}>
          <Button
            color="primary"
            className={showProgress ? "flex-grow-1 me-2" : "w-100"}
            onClick={() => onContinue(course)}
          >
            {showProgress ? <FaPlay className="me-2" /> : <FaCheckCircle className="me-2" />}
            {showProgress ? "Continue" : "Go to Course"}
          </Button>

          {showProgress && (
            <Button outline color="secondary" onClick={() => onUnsubscribe(course)}>
              <FaTimes />
            </Button>
          )}
        </div>
      ) : (
        <Button color="primary" onClick={() => onSubscribe(course)}>
          <FaPlusCircle className="me-2" />
          Subscribe
        </Button>
      )}
    </CardBody>
  </Card>
);

/* ------------------------------------------------------------------ */
/* 4. Main page component                                              */
/* ------------------------------------------------------------------ */
const Courses2 = () => {
  /* ---------- state ---------- */
  const [activeTab, setActiveTab] = useState("available");
  const [subscribed, setSubscribed] = useState([]); // array of courses with progress
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState({ visible: false, message: "" });

  /* ---------- derived lists ---------- */
  const availableCourses = COURSES.filter(
    (c) =>
      !subscribed.some((sc) => sc.id === c.id) &&
      [c.title, c.instructor, c.description].some((field) =>
        field.toLowerCase().includes(search.toLowerCase())
      )
  );

  const myCourses = subscribed.filter((c) =>
    [c.title, c.instructor, c.description].some((field) =>
      field.toLowerCase().includes(search.toLowerCase())
    )
  );

  /* ---------- handlers ---------- */
  const toggleTab = (tab) => setActiveTab(tab);

  const handleSubscribe = (course) => {
    setSubscribed((prev) => [
      ...prev,
      { ...course, progress: Math.floor(Math.random() * 15) + 1 },
    ]);
    showToast(`Successfully subscribed to ${course.title}`);
  };

  const handleUnsubscribe = (course) => {
    setSubscribed((prev) => prev.filter((c) => c.id !== course.id));
    showToast(`Unsubscribed from ${course.title}`);
  };

  const handleContinue = (course) => {
    showToast("Redirecting to course content…");
    // integrate your routing/navigation here
  };

  const showToast = (message) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast({ visible: false, message: "" }), 3000);
  };

  /* ---------- UI ---------- */
  return (
    <Container className="py-5">
      {/* Page heading */}
      <div className="text-center mb-5">
        <h1 className="text-primary fw-bold mb-1">Learning Platform</h1>
        <p className="text-muted">Expand your knowledge with our courses</p>
      </div>

      {/* Tabs */}
      <Nav pills className="justify-content-center mb-4 shadow-sm bg-white rounded">
        <NavItem>
          <NavLink
            className={classNames({ active: activeTab === "available" }, "py-3")}
            onClick={() => toggleTab("available")}
          >
            <FaBookOpen className="me-2" />
            Available Courses
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classNames({ active: activeTab === "mycourses" }, "py-3")}
            onClick={() => toggleTab("mycourses")}
          >
            <FaUserGraduate className="me-2" />
            My Courses
          </NavLink>
        </NavItem>
      </Nav>

      {/* Search bar */}
      <Row className="justify-content-center mb-4">
        <Col xs="12" md="8" lg="6">
          <InputGroup>
            <InputGroupText>
              <FaSearch />
            </InputGroupText>
            <Input
              placeholder="Search courses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Tabs content */}
      <TabContent activeTab={activeTab}>
        {/* ----------------- Available ----------------- */}
        <TabPane tabId="available">
          <h4 className="mb-4">Available Courses</h4>
          <Row xs="1" md="2" lg="3" className="g-4">
            {availableCourses.map((c) => (
              <Col key={c.id}>
                <CourseCard
                  course={c}
                  isSubscribed={false}
                  onSubscribe={handleSubscribe}
                />
              </Col>
            ))}
          </Row>
          {availableCourses.length === 0 && (
            <p className="text-center text-muted">No courses match your search.</p>
          )}
        </TabPane>

        {/* ----------------- My Courses ----------------- */}
        <TabPane tabId="mycourses">
          <h4 className="mb-4">My Courses</h4>

          {myCourses.length === 0 ? (
            <div className="text-center py-5">
              <FaBookOpen size={56} className="text-muted mb-3" />
              <h5 className="fw-medium text-muted">No courses yet</h5>
              <p className="text-muted">
                Browse available courses and start learning today!
              </p>
            </div>
          ) : (
            <Row xs="1" md="2" lg="3" className="g-4">
              {myCourses.map((c) => (
                <Col key={c.id}>
                  <CourseCard
                    course={c}
                    isSubscribed
                    onContinue={handleContinue}
                    onUnsubscribe={handleUnsubscribe}
                    showProgress
                  />
                </Col>
              ))}
            </Row>
          )}
        </TabPane>
      </TabContent>

      {/* Toast notification */}
      <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1060 }}>
        <Toast
          fade
          isOpen={toast.visible}
          className="bg-success text-white align-items-center"
        >
          <ToastHeader
            icon={<FaCheckCircle />}
            toggle={() => setToast({ visible: false, message: "" })}
            className="text-white border-0"
          >
            Success
          </ToastHeader>
          <ToastBody>{toast.message}</ToastBody>
        </Toast>
      </div>
    </Container>
  );
};

export default Courses2;
